/* eslint-disable */
import React, { useEffect, useState } from 'react';
import { Button, Grid } from '@mui/material';
import { CloseIcon } from 'src/icons';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import moment from 'moment';
import useYupValidationResolver from 'src/hooks/useYupValidationResolver';
import * as yup from 'yup';
import { useRoleUser } from 'src/hooks/query/usersByRole';
import { scrollToError } from 'src/utils/FormUtils';
import RHFAutoComplete from '../elements/AutoComplete/RHFAutoComplete';
import RHFEditor from '../elements/Editor/RHFEditor';
import RHFEmailsChips from '../elements/EmailsChips/RHFEmailsChips';
import RHFInput from '../elements/Input/RHFInput';
import RHFDatePicker from '../elements/CustomDatePicker/RHFDatePicker';
import RHFTimePicker from '../elements/CustomTimePicker/RHFTimePicker';

export interface emailValues {
  to: Array<string>;
  cc?: Array<string> | null;
  subject: string;
  content: string;
  otherInterviewers?: Array<string>;
}

export interface RequestGatePassFormValues extends emailValues {
  date: string;
  time: string;
  interviewer: number;
  otherInterviewers?: Array<string>;
}

const validationSchema = (title: string | undefined) => yup.object().shape({
  to: yup.array(yup.string().email()).required('Must have at least 1 email'),
  cc: yup.array(yup.string().email()).optional(),
  subject: yup.string().required('subject required').min(1, 'Subject required'),
  content: yup.string().required('email content required'),
  date: yup.string().required('date is required'),
  time: yup.string().required('time is required'),
  // Conditionally apply interviewer validation based on title
  interviewer: title === 'ONBOARD INSTRUCTIONS' ? yup.number().optional() : yup.number().required('Interviewer is required'),
});

interface RequestGatePassProps {
  onSubmit: (data: emailValues) => void;
  onCancel: () => void;
  loading: boolean;
  defaultValues?: emailValues | undefined;
  title?: string;
}

const RequestGatePass = function RequestGatePass(props: RequestGatePassProps) {
  const {
    onSubmit,
    defaultValues,
    onCancel,
    title,
  } = props;

  const resolver = useYupValidationResolver(validationSchema(title));
  const [disableSubmit, setDisableSubmit] = useState<boolean>(false);

  const { userByRoleOptionsWithEmail, usersLoading } = useRoleUser({
    roleNameList: 'ATSInterviewer',
  });

  const methods = useForm<RequestGatePassFormValues>({
    defaultValues,
    resolver,
  });

  const {
    handleSubmit,
    getValues,
  } = methods;

  const onError = () => {
    scrollToError('request-gatepass-form');
  };

  useEffect(() => {
    // set the how value in the template
    methods.setValue('to', defaultValues?.to || []);
    methods.setValue('cc', defaultValues?.cc);
    methods.setValue('subject', defaultValues?.subject || '');
  }, [defaultValues]);

  const submitHandler: SubmitHandler<RequestGatePassFormValues> = async (data) => {
    setDisableSubmit(true);
    const {
      date = '',
      time = '',
    } = data;

    const interviewDate = date && moment(date).format('MM/DD/YYYY');
    const interviewTime = time && moment(time).format('hh:mm A');
    const request = {
      ...data,
      interviewerId: data?.interviewer,
      date: (interviewDate || ''),
      time: (interviewTime || ''),
    };

    await onSubmit(request);
    setDisableSubmit(false);
  };

  const replaceTemplateValues = (template: string, key: string, value: string) => (
    template.replaceAll(key, value)
  );

  const getValueFromFormAndUpdateTemplate = (dynamicKey: string, value: string) => {
    let modified = replaceTemplateValues(
      defaultValues?.content || '',
      dynamicKey,
      value
    );
    const {
      date,
      time,
      interviewer,
    } = getValues();

    const dynamicKeys = {
      date: title!=='ONBOARD INSTRUCTIONS' ? '[$INTERVIEW_DATE$]' : '[$START_DATE$]',
      time: title!=='ONBOARD INSTRUCTIONS' ? '[$INTERVIEW_TIME$]' : '[$START_TIME$]',
      interviewer: '[$INTERVIEWER_NAME$]',
    };

    const valuesObj = {
      date,
      time,
      interviewer,
    };

    Object.keys(dynamicKeys).map((key) => {
      const typedKey = key as keyof typeof dynamicKeys;
      if (valuesObj[typedKey]) {
        if (key === 'date') {
          modified = replaceTemplateValues(
            modified,
            dynamicKeys[typedKey],
            moment(valuesObj[typedKey]).format('MMMM Do, YYYY'),
          );
        } else if (key === 'time') {
          modified = replaceTemplateValues(
            modified,
            dynamicKeys[typedKey],
            moment(valuesObj[typedKey]).format('hh:mm A'),
          );
        } else if (key === 'interviewer') {
          modified = replaceTemplateValues(
            modified,
            dynamicKeys[typedKey],
            userByRoleOptionsWithEmail.find(({ value }) => value === valuesObj[typedKey])?.label || '',
          );
        }
      }
    });

    methods.setValue('content', modified);
  }

  return (
    <div id="request-gatepass-form">
      <FormProvider {...methods}>
        <form className="bg-white  p-10 flex gap-8 justify-center items-center flex-col">
          {title && (
            <div className="flex justify-center items-center w-full text-[#28364C] font-medium text-base">
              {title}
            </div>
          )}

          <div className="flex w-full p-4 flex-col gap-10">
            <div className="w-full flex-shrink-0">

              <Grid container spacing={3} mb={4}>
                <Grid item xs={12} sm={12} md={6} lg={4}>
                  <RHFDatePicker
                    control={methods.control}
                    name='date'
                    label='Date'
                    minDate={moment(new Date())}
                    onDateChange={(event: any) => {
                      getValueFromFormAndUpdateTemplate(
                        title !== 'ONBOARD INSTRUCTIONS' ? '[$INTERVIEW_DATE$]' : '[$START_DATE$]',
                        event.format('MMMM Do, YYYY'),
                      )  
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={12} md={6} lg={4}>
                  <RHFTimePicker
                    control={methods.control}
                    name='time'
                    label='Time'
                    onTimeChange={(event: any) => {
                      getValueFromFormAndUpdateTemplate(
                        title !== 'ONBOARD INSTRUCTIONS' ? '[$INTERVIEW_TIME$]' : '[$START_TIME$]',
                        event.format('hh:mm A'),
                      );
                    }}
                  />
                </Grid>

                {
                  title !== 'ONBOARD INSTRUCTIONS' && (
                    <Grid item xs={12} sm={12} md={6} lg={4}>
                  <div className="pt-6">
                    <RHFAutoComplete
                      name="interviewer"
                      label="Interviewer"
                      control={methods.control}
                      options={userByRoleOptionsWithEmail}
                      loading={usersLoading}
                      placeholder="Select interviewer"
                      onSelectChange={(event: any) => {
                        getValueFromFormAndUpdateTemplate(
                          '[$INTERVIEWER_NAME$]',
                          userByRoleOptionsWithEmail.find(({ value }) => value === event as number)?.label || '',
                        );
                      }}
                    />
                  </div>
                </Grid>
                  )
                }
              </Grid >
            </div >

            {
              title !== 'ONBOARD INSTRUCTIONS' && (
              <div className="w-full flex-shrink-0">
                <RHFEmailsChips
                  control={methods.control}
                  name="otherInterviewers"
                  label="Additional Interviewers"
                  placeholder="Enter emails"
                />
              </div>
              )
            }
            <div className="w-full flex-shrink-0">
              <RHFEmailsChips
                control={methods.control}
                name="to"
                label="To"
                placeholder="Enter emails"
              />
            </div>
            <div className="w-full flex-shrink-0">
              <RHFEmailsChips
                control={methods.control}
                name="cc"
                label="cc"
                placeholder="Enter emails"
              />
            </div>
            <div className="w-full flex-shrink-0">
              <RHFInput
                control={methods.control}
                name="subject"
                label="Subject"
                placeholder="Subject"
              />
            </div>
            <div className="w-full min-h-[200px] flex-shrink-0">
              <RHFEditor
                control={methods.control}
                name="content"
              />
            </div>
          </div >
          <div className="w-full flex  gap-2 ">
            <Button variant="contained" type="submit" size="small" className="font-medium rounded-full" disabled={disableSubmit} onClick={handleSubmit(submitHandler, onError)}>Send</Button>
            <Button variant="outlined" size="small" className="font-medium rounded-full" onClick={onCancel} disabled={disableSubmit}>Cancel</Button>
          </div>
          <div
            className="absolute right-2 top-2"
            onClick={onCancel}
          >
            <CloseIcon className="cursor-pointer" />
          </div>
        </form >
      </FormProvider >
    </div>
  );
};

RequestGatePass.defaultProps = {
  title: '',
};

RequestGatePass.propTypes = {

};

export default RequestGatePass;
